/* eslint-disable @typescript-eslint/no-restricted-imports */
import React, { useEffect, useRef, useState } from "react";
import { Redirect, useLocation } from "react-router-dom";
import { connect, useSelector } from "react-redux";
import type { InjectedFormProps, DecoratedFormProps } from "redux-form";
import { reduxForm, formValueSelector, isDirty } from "redux-form";
import {
  LOGIN_FORM_NAME,
  LOGIN_FORM_EMAIL_FIELD_NAME,
  LOGIN_FORM_PASSWORD_FIELD_NAME,
  LOGIN_FORM_CAPTCHA_FIELD_NAME,
  LOGIN_FORM_NAME_FIELD_NAME,
} from "ee/constants/forms";
import { FORGOT_PASSWORD_URL, SETUP, SIGN_UP_URL } from "constants/routes";
import {
  LOGIN_PAGE_TITLE,
  LOGIN_PAGE_PASSWORD_INPUT_PLACEHOLDER,
  LOGIN_PAGE_EMAIL_INPUT_PLACEHOLDER,
  FORM_VALIDATION_EMPTY_PASSWORD,
  FORM_VALIDATION_INVALID_EMAIL,
  LOGIN_PAGE_LOGIN_BUTTON_TEXT,
  LOGIN_PAGE_FORGOT_PASSWORD_TEXT,
  LOGIN_PAGE_REFRESH_CAPTCHA_TEXT,
  LOGIN_PAGE_CAPTCHA_INPUT_PLACEHOLDER,
  LOGIN_PAGE_SIGN_UP_LINK_TEXT,
  LOGIN_PAGE_INVALID_CREDS_ERROR,
  NEW_TO_APPSMITH,
  createMessage,
  LOGIN_PAGE_SUBTITLE,
  LOGIN_PAGE_USERNAME_INPUT_PLACEHOLDER,
  FORM_VALIDATION_EMPTY_NAME,
} from "ee/constants/messages";
import { FormGroup } from "design-system-old";
import { Button, Link } from "design-system";
import { message, Tabs, type TabsProps, ConfigProvider } from "antd";
import FormTextField from "components/utils/ReduxFormTextField";
import ThirdPartyAuth from "pages/UserAuth/ThirdPartyAuth";
import { isEmail, isEmptyString } from "utils/formhelpers";
import type { LoginFormValues } from "pages/UserAuth/helpers";

import { SpacedSubmitForm, FormActions } from "pages/UserAuth/StyledComponents";
import AnalyticsUtil from "utils/AnalyticsUtil";
import { LOGIN_SUBMIT_PATH } from "ee/constants/ApiConstants";
import PerformanceTracker, {
  PerformanceTransactionName,
} from "utils/PerformanceTracker";
import { getIsSafeRedirectURL } from "utils/helpers";
import { getCurrentUser } from "selectors/usersSelectors";
import Container from "pages/UserAuth/Container";
import UserApi from "ee/api/UserApi";
import {
  getIsFormLoginEnabled,
  getTenantConfig,
  getIsFormSignupEnable,
} from "ee/selectors/tenantSelectors";
import { useFeatureFlag } from "utils/hooks/useFeatureFlag";
import { FEATURE_FLAG } from "ee/entities/FeatureFlag";
import { getHTMLPageTitle } from "ee/utils/BusinessFeatures/brandingPageHelpers";
import Helmet from "react-helmet";
import FooterLinks from "pages/UserAuth/FooterLinks";
import { FormIcons } from "icons/FormIcons";
import styled, { css } from "styled-components";
import type { User } from "constants/userConstants";
import { getAppsmithConfigs } from "ee/configs";
const { enableSso } = getAppsmithConfigs();
export const LoginForm = styled.div`
  input {
    padding-left: 30px;
  }
`;

export const LoginBottom = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 16px;
  a > span {
    font-size: 16px;
  }
`;

const CommonIconStyles = css`
  position: absolute;
  top: 17px;
  left: 8px;
`;

export const StyledEmailIcon = styled(FormIcons.EMAIL_ICON)`
  ${CommonIconStyles}
  &&& svg {
    path {
      fill: none;
    }
  }
`;

export const StyledUserIcon = styled(FormIcons.USER_ICON)`
  ${CommonIconStyles}
  &&& svg {
    path {
      fill: #8a9997;
    }
  }
`;

export const StyledSecureIcon = styled(FormIcons.SECURE_ICON)`
  ${CommonIconStyles}
  &&& svg {
    path {
      fill: #8a9997;
    }
  }
`;

export const StyledEyeOnIcon = styled(FormIcons.EYE_ON_ICON)`
  ${CommonIconStyles}
  &&& svg {
    cursor: pointer;
    path {
      fill: #8a9997;
    }
  }
`;

export const StyledEyeOffIcon = styled(FormIcons.EYE_OFF_ICON)`
  ${CommonIconStyles}
  &&& svg {
    cursor: pointer;
    path {
      fill: #8a9997;
    }
  }
`;

const AntdTabs = styled(Tabs)`
  &&& .ant-tabs-nav::before {
    border-bottom: none;
  }
  .ant-tabs-tab-active .ant-tabs-tab-btn {
  }
`;

const validate = (values: LoginFormValues, props: ValidateProps) => {
  const errors: LoginFormValues = {};
  const email = values[LOGIN_FORM_EMAIL_FIELD_NAME] || "";
  const password = values[LOGIN_FORM_PASSWORD_FIELD_NAME];
  const name = values[LOGIN_FORM_NAME_FIELD_NAME] || "";
  const activeKey = values.activeKey;
  // const captcha = values[LOGIN_FORM_CAPTCHA_FIELD_NAME];
  const { isPasswordFieldDirty, touch, activeTab } = props;
  if (!password || isEmptyString(password)) {
    isPasswordFieldDirty && touch?.(LOGIN_FORM_PASSWORD_FIELD_NAME);
    errors[LOGIN_FORM_PASSWORD_FIELD_NAME] = createMessage(
      FORM_VALIDATION_EMPTY_PASSWORD,
    );
  }
  if (activeKey === "1") {
    if (!isEmptyString(email) && !isEmail(email)) {
      touch?.(LOGIN_FORM_EMAIL_FIELD_NAME);
      errors[LOGIN_FORM_EMAIL_FIELD_NAME] = createMessage(
        FORM_VALIDATION_INVALID_EMAIL,
      );
    }
  }
  if (activeKey === "2") {
    if (!isEmptyString(name) && !/^[a-zA-Z][a-zA-Z0-9_-]{3,99}$/.test(name)) {
      touch?.(LOGIN_FORM_NAME_FIELD_NAME);
      errors[LOGIN_FORM_NAME_FIELD_NAME] = createMessage(
        FORM_VALIDATION_EMPTY_NAME,
      );
    }
  }
  // if (isEmptyString(captcha)) {
  // touch?.(LOGIN_FORM_CAPTCHA_FIELD_NAME);
  // errors[LOGIN_FORM_CAPTCHA_FIELD_NAME] = createMessage(
  //   FORM_VALIDATION_INVALID_CAPTCHA,
  // );
  // }

  return errors;
};

type LoginFormProps = {
  emailValue?: string;
  captcha?: string;
  tabKey?: string;
  onChangTab?: (key: string) => void;
  nameValue?: string;
} & InjectedFormProps<LoginFormValues, { emailValue: string }>;

type ValidateProps = {
  isPasswordFieldDirty?: boolean;
} & DecoratedFormProps<
  LoginFormValues,
  {
    emailValue: string;
    isPasswordFieldDirty?: boolean;
    activeKey?: string;
    activeTab?: string;
  }
>;

export function Login(props: LoginFormProps) {
  const { captcha, emailValue: email, error, valid, nameValue: name } = props;
  const location = useLocation();
  const isFormLoginEnabled = useSelector(getIsFormLoginEnabled);
  const isFormSignupEnabled = useSelector(getIsFormSignupEnable);
  // const isFormSignupEnabled = !window.APPSMITH_FEATURE_CONFIGS.signupDisabled;
  const queryParams = new URLSearchParams(location.search);
  const [captchaBlobUrl, setCaptchaBlob] = useState<any>();
  const [tabKey, setTabKey] = useState<string>("1");
  const isFormValid =
    tabKey === "1"
      ? valid && email && !isEmptyString(email) && captcha
      : valid && name && !isEmptyString(name) && captcha;

  const fetchCaptcha = async () => {
    const res: any = await UserApi.fetchCaptcha();
    const codeImg = window.URL.createObjectURL(res);
    setCaptchaBlob(codeImg);
  };
  useEffect(() => {
    fetchCaptcha();
  }, []);
  const isBrandingEnabled = useFeatureFlag(
    FEATURE_FLAG.license_branding_enabled,
  );
  const tentantConfig = useSelector(getTenantConfig);
  const { instanceName } = tentantConfig;
  const htmlPageTitle = getHTMLPageTitle(isBrandingEnabled, instanceName);

  let showError = false;
  let errorMessage = "";
  const currentUser: User | undefined = useSelector(getCurrentUser);
  const [isShowPassword, setIsShowPassword] = useState<boolean>(false);

  useEffect(() => {
    if (showError) {
      message.open({
        type: "error",
        duration: 5,
        content:
          !!errorMessage && errorMessage !== "true"
            ? errorMessage
            : createMessage(LOGIN_PAGE_INVALID_CREDS_ERROR),
        className: "my-msg",
      });
    }
    return () => {
      message.destroy();
    };
  }, []);

  if (currentUser?.emptyInstance) {
    return <Redirect to={SETUP} />;
  }
  if (queryParams.get("error")) {
    errorMessage = queryParams.get("message") || queryParams.get("error") || "";
    showError = true;
    if (errorMessage === "true") {
      errorMessage = "账号密码错误，请重新输入";
    }
    // const savedTabKey = localStorage.getItem("loginTabKey");
    // if (savedTabKey) {
    //   setTabKey(savedTabKey);
    // }
  }
  let loginURL = "/api/v1/" + LOGIN_SUBMIT_PATH;
  let signupURL = SIGN_UP_URL;
  const redirectUrl = queryParams.get("redirectUrl");
  if (redirectUrl != null && getIsSafeRedirectURL(redirectUrl)) {
    const encodedRedirectUrl = encodeURIComponent(redirectUrl);
    loginURL += `?redirectUrl=${encodedRedirectUrl}`;
    signupURL += `?redirectUrl=${encodedRedirectUrl}`;
  }

  let forgotPasswordURL = `${FORGOT_PASSWORD_URL}`;
  if (props.emailValue && !isEmptyString(props.emailValue)) {
    forgotPasswordURL += `?email=${props.emailValue}`;
  }

  useEffect(() => {
    const savedTabKey = localStorage.getItem("loginTabKey");
    if (savedTabKey) {
      setTabKey(savedTabKey);
    }
  }, []);

  // 第三方登录
  const footerSection = () =>
    enableSso ? (
      <div className="w-[min(400px,80%)] rounded-[var(--ads-v2\-border-radius)]  border-[color:var(--ads-v2\-color-border)] .login-bg">
        <FooterLinks />
      </div>
    ) : (
      <></>
    );

  const emailFieldRef = useRef(null);

  const renderLoginForm = (type: string) => {
    return (
      <>
        {isFormLoginEnabled && (
          <>
            <form
              action={loginURL}
              method="POST"
              onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
                e.preventDefault();
                const form = e.nativeEvent.target as HTMLFormElement;
                const formData = new FormData(form);
                const inputs = document.querySelectorAll("input");
                const inputValues: any = {};
                inputs.forEach((input) => {
                  if (
                    input.name === "email" ||
                    input.name === "password" ||
                    input.name === "name"
                  ) {
                    inputValues[input.name] = btoa(input.value);
                  } else {
                    inputValues[input.name] = input.value;
                  }
                });
                fetch(form.action, {
                  method: form.method,
                  body: new URLSearchParams(inputValues),
                  headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                  },
                }).then(async (response) => {
                  // 清空 loginTabKey
                  localStorage.removeItem("loginTabKey");
                  if (response.ok) {
                    const redirectUrl = queryParams.get("redirectUrl");
                    const url = new URL(response.url);
                    const hasError = url.searchParams.get("error") === "true";
                    if (hasError) {
                      // 保存当前的 tabKey 到 localStorage
                      localStorage.setItem("loginTabKey", tabKey);
                    }
                    window.location.href = response.url;
                  }
                });
              }}
            >
              <FormGroup
                intent={error ? "danger" : "none"}
                style={{ position: "relative" }}
              >
                <LoginForm>
                  <FormTextField
                    autoFocus
                    className="pp-height login-form"
                    name={type === "email" ? "email" : "name"}
                    placeholder={createMessage(
                      type === "email"
                        ? LOGIN_PAGE_EMAIL_INPUT_PLACEHOLDER
                        : LOGIN_PAGE_USERNAME_INPUT_PLACEHOLDER,
                    )}
                    type="text"
                  />
                </LoginForm>
                {type === "email" ? (
                  <StyledEmailIcon height={15} width={15} />
                ) : (
                  <StyledUserIcon height={15} width={15} />
                )}
              </FormGroup>
              <FormGroup
                intent={error ? "danger" : "none"}
                style={{ position: "relative" }}
              >
                <LoginForm>
                  <FormTextField
                    className="pp-height"
                    name={LOGIN_FORM_PASSWORD_FIELD_NAME}
                    placeholder={createMessage(
                      LOGIN_PAGE_PASSWORD_INPUT_PLACEHOLDER,
                    )}
                    type={isShowPassword ? "text" : "password"}
                  />
                </LoginForm>
                {isShowPassword ? (
                  <StyledEyeOnIcon
                    height={15}
                    onClick={() => setIsShowPassword(false)}
                    width={15}
                  />
                ) : (
                  <StyledEyeOffIcon
                    height={15}
                    onClick={() => setIsShowPassword(true)}
                    width={15}
                  />
                )}
              </FormGroup>
              <FormGroup intent={"none"}>
                <div className="flex flex-col [@media(min-width:450px)]:flex-row justify-between">
                  <div className="w-full [@media(min-width:456px)]:w-2/3">
                    <LoginForm>
                      <FormTextField
                        className="pp-height"
                        name={LOGIN_FORM_CAPTCHA_FIELD_NAME}
                        placeholder={createMessage(
                          LOGIN_PAGE_CAPTCHA_INPUT_PLACEHOLDER,
                        )}
                        type="text"
                      />
                    </LoginForm>
                    <StyledSecureIcon height={15} width={15} />
                  </div>
                  <div className="w-full [@media(min-width:450px)]:w-1/3 flex justify-end mt-2 [@media(min-width:450px)]:mt-0">
                    <a
                      onClick={async () => fetchCaptcha()}
                      className="flex items-center"
                    >
                      <img
                        alt={"captcha"}
                        src={captchaBlobUrl}
                        style={{
                          width: "100px",
                          height: "45px",
                          cursor: "pointer",
                        }}
                        title={createMessage(LOGIN_PAGE_REFRESH_CAPTCHA_TEXT)}
                      />
                    </a>
                  </div>
                </div>
              </FormGroup>

              <FormActions className="pp-height pp-font">
                <Button
                  isDisabled={!isFormValid}
                  kind="primary"
                  onClick={() => {
                    PerformanceTracker.startTracking(
                      PerformanceTransactionName.LOGIN_CLICK,
                    );
                    AnalyticsUtil.logEvent("LOGIN_CLICK", {
                      loginMethod: "EMAIL",
                    });
                  }}
                  size="md"
                  type="submit"
                >
                  {createMessage(LOGIN_PAGE_LOGIN_BUTTON_TEXT)}
                </Button>
              </FormActions>
            </form>
            <LoginBottom>
              {isFormSignupEnabled ? (
                <div className="flex mt-[24px]">
                  {createMessage(NEW_TO_APPSMITH)}
                  <Link
                    className="t--sign-up t--signup-link"
                    kind="primary"
                    target="_self"
                    to={signupURL}
                  >
                    <span className="text-base">
                      {createMessage(LOGIN_PAGE_SIGN_UP_LINK_TEXT)}
                    </span>
                  </Link>
                </div>
              ) : (
                <div />
              )}
              <div className="mt-[24px]">
                <Link
                  className="justify-center"
                  kind="primary"
                  target="_self"
                  to={forgotPasswordURL}
                >
                  <span className="text-base">
                    {createMessage(LOGIN_PAGE_FORGOT_PASSWORD_TEXT)}
                  </span>
                </Link>
              </div>
            </LoginBottom>
          </>
        )}
      </>
    );
  };

  useEffect(() => {
    props.initialize({ activeKey: tabKey } as any);
  }, [tabKey]);

  const onChangTab = (key: string) => {
    setTabKey(key);
  };

  return (
    <Container
      footer={footerSection()}
      subtitle={createMessage(LOGIN_PAGE_SUBTITLE)}
      title={createMessage(LOGIN_PAGE_TITLE)}
    >
      <Helmet>
        <title>{htmlPageTitle}</title>
      </Helmet>
      {/* 账号密码 */}
      <ConfigProvider
        theme={{
          components: {
            Tabs: {
              /* 这里是你的组件 token */
              titleFontSize: 16,
            },
          },
        }}
      >
        <AntdTabs activeKey={tabKey} centered onChange={onChangTab}>
          <Tabs.TabPane key="1" tab="邮箱登录">
            {renderLoginForm("email")}
          </Tabs.TabPane>
          <Tabs.TabPane key="2" tab="用户名登录">
            {renderLoginForm("username")}
          </Tabs.TabPane>
        </AntdTabs>
      </ConfigProvider>
    </Container>
  );
}

const selector = formValueSelector(LOGIN_FORM_NAME);
export default connect((state) => ({
  emailValue: selector(state, LOGIN_FORM_EMAIL_FIELD_NAME),
  nameValue: selector(state, LOGIN_FORM_NAME_FIELD_NAME),
  isPasswordFieldDirty: isDirty(LOGIN_FORM_NAME)(
    state,
    LOGIN_FORM_PASSWORD_FIELD_NAME,
  ),
  captcha: selector(state, LOGIN_FORM_CAPTCHA_FIELD_NAME),
}))(
  reduxForm<
    LoginFormValues,
    {
      emailValue: string;
      activeKey?: string;
      nameValue?: string;
    }
  >({
    validate: (values, props) =>
      validate(values, { ...props, activeKey: props.activeKey }),
    touchOnBlur: false,
    form: LOGIN_FORM_NAME,
  })(Login),
);
